import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Key,
  Fingerprint,
  FileCheck,
  CheckCircle2,
  ArrowRight,
  Building2,
  CreditCard,
  Smartphone,
  BadgeCheck,
  X,
  Minus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import HeroLiveDemo from '../components/HeroLiveDemo';
import BookDemoModal from '../components/BookDemoModal';
import ContactSalesModal from '../components/ContactSalesModal';
import { useLanguage } from '../context/LanguageContext';

// Philippine-specific features
const coreFeatures = [
  {
    icon: FileCheck,
    title: 'Advanced OCR Extraction',
    titleFil: 'Advanced OCR Extraction',
    description: 'Automated data extraction from 11+ Philippine IDs with 99.9% accuracy - no more manual typing.',
    descriptionFil: 'Awtomatikong pagkuha ng datos mula sa 11+ na Philippine IDs na may 99.9% accuracy.'
  },
  {
    icon: 'logo',
    title: 'QR & Barcode Validation',
    titleFil: 'QR & Barcode Validation',
    description: 'Multi-format barcode scanning for PhilID (QR), Driver\'s License (PDF417), and Postal ID - protected against tampered physical IDs.',
    descriptionFil: 'Multi-format barcode scanning para sa PhilID (QR), Driver\'s License (PDF417), at Postal ID - protektado laban sa tampered na physical IDs.'
  },
  {
    icon: Fingerprint,
    title: 'Biometric Verification',
    titleFil: 'Biometric Verification',
    description: 'Face matching with liveness detection and anti-spoofing to prevent identity theft.',
    descriptionFil: 'Face matching na may liveness detection at anti-spoofing.'
  },
  {
    icon: Key,
    title: 'Cryptographic Audit Trail',
    titleFil: 'Cryptographic Audit Trail',
    description: 'Secure SHA-256 hashing for immutable verification records and enterprise compliance.',
    descriptionFil: 'Secure na SHA-256 hashing para sa immutable verification records.'
  }
];

// Philippine IDs supported
const supportedIds = [
  { name: 'PhilID', full: 'Philippine National ID', agency: 'PSA', status: 'QR + Template Match' },
  { name: 'Passport', full: 'Philippine Passport', agency: 'DFA', status: 'MRZ + Biometric' },
  { name: "Driver's License", full: 'LTO License', agency: 'LTO', status: 'PDF417 Scan' },
  { name: 'UMID', full: 'Unified Multi-Purpose ID', agency: 'SSS/GSIS', status: 'OCR Assessment' },
  { name: 'Postal ID', full: 'Philippine Postal ID', agency: 'PHLPost', status: 'QR Scan' },
  { name: 'PRC ID', full: 'Professional License', agency: 'PRC', status: 'OCR Assessment' },
  { name: 'TIN Card', full: 'Tax Identification Number', agency: 'BIR', status: 'OCR Assessment' },
  { name: 'PhilHealth', full: 'PhilHealth ID', agency: 'PhilHealth', status: 'OCR Assessment' },
  { name: "Voter's ID", full: 'COMELEC ID', agency: 'COMELEC', status: 'OCR Assessment' },
];

// Payment methods
const paymentMethods = [
  { name: 'Bank QR / InstaPay', users: 'All PH banks' },
  { name: 'GCash', users: 'Coming soon' },
  { name: 'Maya', users: 'Coming soon' },
];

// Use cases for Philippine market
const useCases = [
  {
    icon: CreditCard,
    title: 'Online Lending & BNPL',
    titleFil: 'Online Lending & BNPL',
    description: 'Verify borrowers, block synthetic IDs, and keep an AMLA-ready audit trail — built for SEC-registered lending apps.',
    clients: ['Borrower KYC', 'Fraud screening', 'AMLA audit trail']
  },
  {
    icon: Building2,
    title: 'Digital Banks & Fintechs',
    titleFil: 'Digital Banks & Fintechs',
    description: 'BSP eKYC-ready onboarding with PhilSys and 11 Philippine government IDs.',
    clients: ['BSP eKYC-ready', 'PhilSys-native', 'Pilot-ready']
  },
  {
    icon: Smartphone,
    title: 'Marketplaces & E-Commerce',
    titleFil: 'Marketplaces & E-Commerce',
    description: 'Seller and buyer verification to reduce COD and account fraud.',
    clients: ['Seller KYC', 'Anti-fraud', 'Fast onboarding']
  },
  {
    icon: BadgeCheck,
    title: 'HR & Gig Platforms',
    titleFil: 'HR & Gig Platforms',
    description: 'Confirm identity for hires and gig workers in seconds.',
    clients: ['Applicant checks', 'Worker onboarding', 'Records']
  }
];

// Credit bundle pricing
const creditBundles = [
  {
    name: 'Personal',
    price: '100',
    basicCredits: 4,
    enhancedCredits: 2,
    bonus: '0%',
    effectivePrice: '₱25',
    forIndividuals: true,
  },
  {
    name: 'Starter',
    price: '500',
    basicCredits: 20,
    enhancedCredits: 11,
    bonus: '0%',
    effectivePrice: '₱25',
  },
  {
    name: 'Business',
    price: '2,500',
    basicCredits: 120,
    enhancedCredits: 66,
    bonus: '+20%',
    effectivePrice: '₱21',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: '5,000',
    basicCredits: 260,
    enhancedCredits: 143,
    bonus: '+30%',
    effectivePrice: '₱19',
  },
];

// Compliance badges
const complianceBadges = [
  { name: 'BSP eKYC Compliance-Ready', description: 'Bangko Sentral ng Pilipinas' },
  { name: 'Registration in Progress', description: 'National Privacy Commission' },
  { name: 'AMLA Compliant', description: 'Anti-Money Laundering Act' },
  { name: 'Security-First Architecture', description: '24-hr Purge • Encrypted • Audit Trails' },
];

// Comparison table rows
const comparisonRows = [
  {
    feature: 'Price per verification',
    featureFil: 'Presyo bawat verification',
    agos: '₱19–25 (bundles)',
    vendors: '₱110 – ₱330',
    agosHighlight: true,
  },
  {
    feature: 'PhilSys QR Validation',
    featureFil: 'PhilSys QR Validation',
    sub: 'Format check + data extraction',
    subFil: 'Format check + data extraction',
    agos: 'QR Format Validation',
    agosSubtext: 'Data extraction included',
    vendors: null,
  },
  {
    feature: 'Local Payment (Bank QR / InstaPay)',
    featureFil: 'Local Payment (Bank QR / InstaPay)',
    agos: true,
    vendors: false,
    vendorNote: 'Credit card only',
  },
  {
    feature: 'BSP eKYC Ready',
    featureFil: 'BSP eKYC Ready',
    agos: true,
    vendors: 'partial',
    vendorNote: 'Manual compliance',
  },
  {
    feature: 'Philippine ID Templates',
    featureFil: 'Philippine ID Templates',
    agos: '11 templates',
    vendors: '3–5 templates',
    agosHighlight: true,
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// ── Comparison Table ─────────────────────────────────────────────────────────
function ComparisonTable({ lang }: { lang: 'en' | 'fil' }) {
  return (
    <div className="rounded-2xl border border-slate-700/50 overflow-hidden bg-slate-900/50 backdrop-blur-sm">
      {/* Header */}
      <div className="grid grid-cols-3 border-b border-slate-700/50">
        <div className="py-4 px-5 text-sm text-slate-500 font-medium">
          {lang === 'en' ? 'Feature' : 'Feature'}
        </div>
        <div className="py-4 px-5 text-center border-x border-slate-700/50 bg-teal-500/5">
          <span className="text-sm font-bold text-teal-400">Agos Identity</span>
          <div className="text-[10px] text-teal-500/70 font-medium mt-0.5">Local · Affordable</div>
        </div>
        <div className="py-4 px-5 text-center">
          <span className="text-sm font-medium text-slate-500">
            {lang === 'en' ? 'International Vendors' : 'International Vendors'}
          </span>
          <div className="text-[10px] text-slate-600 mt-0.5">Jumio · Onfido · Veriff</div>
        </div>
      </div>

      {/* Rows */}
      {comparisonRows.map((row, i) => (
        <div
          key={i}
          className={`grid grid-cols-3 border-b border-slate-800/60 last:border-0 transition-colors hover:bg-slate-800/20 ${
            i % 2 === 0 ? '' : 'bg-slate-900/30'
          }`}
        >
          {/* Feature name */}
          <div className="py-4 px-5 flex flex-col justify-center">
            <span className="text-sm text-slate-300 font-medium">
              {lang === 'en' ? row.feature : row.featureFil}
            </span>
            {row.sub && (
              <span className="text-[10px] text-slate-600 mt-0.5">
                {lang === 'en' ? row.sub : row.subFil}
              </span>
            )}
          </div>

          {/* Agos value */}
          <div className="py-4 px-5 border-x border-slate-700/30 bg-teal-500/5 flex flex-col items-center justify-center text-center">
            {row.agos === true ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <div>
                <span className={`text-sm font-semibold ${row.agosHighlight ? 'text-teal-400' : 'text-emerald-400'}`}>
                  {row.agos}
                </span>
                {row.agosSubtext && (
                  <p className="text-[10px] text-slate-500 mt-0.5">{row.agosSubtext}</p>
                )}
              </div>
            )}
          </div>

          {/* Vendor value */}
          <div className="py-4 px-5 flex flex-col items-center justify-center text-center">
            {row.vendors === true ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : row.vendors === false ? (
              <div className="flex flex-col items-center gap-0.5">
                <X className="w-4 h-4 text-red-400/70" />
                {row.vendorNote && <span className="text-[10px] text-slate-600">{row.vendorNote}</span>}
              </div>
            ) : row.vendors === 'partial' ? (
              <div className="flex flex-col items-center gap-0.5">
                <Minus className="w-4 h-4 text-yellow-500/70" />
                {row.vendorNote && <span className="text-[10px] text-slate-600">{row.vendorNote}</span>}
              </div>
            ) : row.vendors === null ? (
              <div className="flex flex-col items-center gap-0.5">
                <X className="w-4 h-4 text-red-400/70" />
                <span className="text-[10px] text-slate-600">
                  {lang === 'en' ? 'Not available' : 'Hindi available'}
                </span>
              </div>
            ) : (
              <span className="text-sm text-slate-500">{row.vendors}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function LandingPage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { lang: language } = useLanguage();

  const getText = (en: string, fil: string) => language === 'en' ? en : fil;

  return (
    <div className="relative w-full overflow-x-hidden bg-slate-950">
      {/* Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-teal-950/20 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(20,184,166,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(6,182,212,0.08),transparent_55%)]" />
      </div>

      {/* ── Hero ── */}
      <section className="relative z-10 min-h-screen flex flex-col items-center justify-start px-4 pt-24 pb-16">
        <div className="max-w-4xl mx-auto w-full flex flex-col items-center text-center mt-8 lg:mt-12">

          {/* NPC Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full mb-8 backdrop-blur-sm"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">NPC Registration in Progress · DPA Ready</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          >
            <span className="text-white">
              {getText('Borrower verification for Philippine lending apps', 'Borrower verification para sa Philippine lending apps')}
            </span>
            <br />
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent block mt-1">
              {getText('in seconds — without enterprise pricing', 'sa ilang segundo — walang enterprise pricing')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg md:text-xl text-slate-400 mb-6 max-w-3xl mx-auto leading-relaxed"
          >
            {getText(
              'Agos verifies your borrowers with Philippine IDs, biometric face-match and liveness, and a tamper-proof audit trail — cutting fraud and onboarding drop-off. Pay only for what you verify, from ₱100. No contract, hosted in Manila.',
              'Bini-verify ng Agos ang iyong mga borrower gamit ang Philippine IDs, biometric face-match at liveness, at tamper-proof audit trail — binabawasan ang fraud at onboarding drop-off. Magbayad lang sa bawat verification, mula ₱100. Walang kontrata, naka-host sa Manila.'
            )}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-teal-400/80 mb-8 font-medium tracking-wide uppercase"
          >
            {getText(
              'Built with Philippine regulatory frameworks in mind — BSP eKYC, AMLA, and NPC data privacy.',
              'Binuo na isinasaalang-alang ang BSP eKYC, AMLA guidelines at NPC data privacy principles.'
            )}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-10"
          >
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/sandbox"
                className="group px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-teal-900/30"
              >
                {getText('Run a live verification', 'Subukan ang live verification')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.button
              type="button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsDemoModalOpen(true)}
              className="px-8 py-4 bg-slate-800/50 border border-slate-700 text-slate-300 rounded-xl font-semibold hover:bg-slate-800 hover:border-slate-600 transition-all duration-300 backdrop-blur-sm"
            >
              {getText('Talk to the founder', 'Makipag-usap sa founder')}
            </motion.button>
          </motion.div>

          {/* Pricing preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-4"
          >
            <span className="text-slate-500 text-sm">{getText('Credit bundles from', 'Credit bundles mula')}</span>
            <span className="text-white font-bold text-sm">₱100 (₱25/verification)</span>
            <span className="text-slate-600 text-sm">·</span>
            <span className="text-slate-400 text-sm">{getText('Pay via Bank QR / InstaPay', 'Magbayad via Bank QR / InstaPay')}</span>
            <span className="text-slate-600 text-sm">·</span>
            <span className="text-emerald-400 text-sm font-medium">{getText('vs ₱110–330 from international vendors', 'vs ₱110–330 mula sa international vendors')}</span>
          </motion.div>
        </div>

        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="w-full max-w-5xl mx-auto px-4 sm:px-8 lg:px-0"
        >
          <HeroLiveDemo />
        </motion.div>
      </section>

      {/* ── Supported IDs + Features wrapper ── */}
      <div className="relative z-10 bg-gradient-to-br from-slate-900 via-teal-950/20 to-slate-900 bg-[length:200%_200%] animate-gradient">

        {/* Supported IDs */}
        <section className="relative z-10 py-16 px-4 border-t border-slate-800/50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp} className="text-center mb-10"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {getText('Supported Government IDs', 'Mga Suportadong Government IDs')}
              </h2>
              <p className="text-slate-400">
                {getText('All major Philippine identification documents', 'Lahat ng major na Philippine identification documents')}
              </p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={staggerContainer}
              className="flex flex-wrap justify-center gap-3"
            >
              {supportedIds.map((id) => (
                <motion.div
                  key={id.name}
                  variants={scaleIn}
                  whileHover={{
                    scale: 1.05,
                    borderColor: 'rgba(20,184,166,0.5)',
                    backgroundColor: 'rgba(15,23,42,0.95)',
                  }}
                  className="group bg-slate-800/80 border border-slate-700 rounded-xl px-6 py-5 backdrop-blur-md cursor-default transition-all duration-300 min-w-[200px] shadow-lg hover:shadow-teal-500/10"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-start gap-4">
                      <span className="text-white font-extrabold text-lg tracking-tight">{id.name}</span>
                      <span className="shrink-0 text-[10px] text-teal-300 font-bold uppercase tracking-wider bg-teal-900/50 border border-teal-700/30 px-2 py-1 rounded-md">
                        {id.status}
                      </span>
                    </div>
                    <div className="text-slate-300 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-teal-400" />
                      {id.agency} Issued
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Core Features */}
        <section className="relative z-10 py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp} className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {getText('Enterprise-Grade Verification', 'Enterprise-Grade na Verification')}
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {getText(
                  'Engineered for BSP eKYC Compliance. Built for NPC-ready architecture with localized data residency.',
                  'Dinisenyo para sa BSP eKYC Compliance. Ginawa para sa NPC-ready architecture na may localized data residency.'
                )}
              </p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ y: -5, borderColor: 'rgba(20,184,166,0.3)' }}
                  className="group bg-slate-800/20 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500/20 transition-colors">
                    {feature.icon === 'logo' ? (
                      <img src="/pwa-192x192.png" alt="Agos Identity Logo" className="w-6 h-6 object-contain" />
                    ) : (
                      <feature.icon className="w-6 h-6 text-teal-400" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {getText(feature.title, feature.titleFil)}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {getText(feature.description, feature.descriptionFil)}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </div>

      {/* ── Use Cases ── */}
      <section className="relative z-10 py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp} className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Built for Philippine Industries', 'Ginawa para sa Philippine Industries')}
            </h2>
            <p className="text-slate-400">
              {getText('Purpose-built for online lenders — and ready for fintechs, marketplaces, and platforms', 'Ginawa para sa mga online lender — handa rin para sa fintech, marketplace, at platform')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, borderColor: 'rgba(20,184,166,0.25)' }}
                className="bg-gradient-to-b from-slate-800/40 to-slate-800/20 border border-slate-700/50 rounded-2xl p-6 backdrop-blur-sm transition-all duration-300"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500/20 to-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {getText(useCase.title, useCase.titleFil)}
                </h3>
                <p className="text-slate-400 text-sm mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-1">
                  {useCase.clients.map((client, i) => (
                    <span key={i} className="text-xs text-slate-500 bg-slate-800/50 px-2 py-1 rounded">
                      {client}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp} className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Credit Bundle Pricing', 'Credit Bundle Pricing')}
            </h2>
            <p className="text-slate-400">
              {getText('Buy verification credits in bundles. Save more with larger bundles.', 'Bumili ng verification credits sa bundles. Mas makatipid sa mas malalaking bundle.')}
            </p>
          </motion.div>

          {/* Bundle cards */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
          >
            {creditBundles.map((bundle) => (
              <motion.div
                key={bundle.name}
                variants={scaleIn}
                whileHover={{ scale: 1.02 }}
                className={`bg-slate-800/30 border rounded-xl p-5 backdrop-blur-sm transition-all duration-300 ${
                  bundle.featured
                    ? 'border-teal-500/50 ring-1 ring-teal-500/20 shadow-lg shadow-teal-900/20'
                    : 'border-slate-700/50'
                }`}
              >
                {bundle.featured && (
                  <div className="text-xs text-teal-400 font-bold mb-2 text-center uppercase tracking-wider">
                    Popular
                  </div>
                )}
                <div className="text-center mb-3">
                  <div className="text-white font-semibold text-lg">{bundle.name}</div>
                  {bundle.bonus !== '0%' && (
                    <div className="text-emerald-400 text-xs font-medium">{bundle.bonus} bonus credits</div>
                  )}
                </div>
                <div className="text-center mb-4">
                  <span className="text-lg text-slate-400">₱</span>
                  <span className="text-3xl font-bold text-white">{bundle.price}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>{getText('Basic credits', 'Basic credits')}</span>
                    <span className="text-white font-medium">{bundle.basicCredits}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>{getText('Enhanced credits', 'Enhanced credits')}</span>
                    <span className="text-white font-medium">{bundle.enhancedCredits}</span>
                  </div>
                  <div className="border-t border-slate-700/50 pt-2 mt-2">
                    <div className="flex justify-between text-slate-400">
                      <span>{getText('Effective price', 'Effective price')}</span>
                      <span className="text-emerald-400 font-medium">{bundle.effectivePrice}/ea</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Payment methods */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="flex flex-wrap justify-center items-center gap-4 mb-10"
          >
            <span className="text-slate-500 text-sm">{getText('Pay with:', 'Magbayad gamit ang:')}</span>
            {paymentMethods.map((method) => (
              <div key={method.name} className="flex items-center gap-1 text-slate-400 text-sm">
                <span>{method.name}</span>
                {method.users && <span className="text-slate-600 text-xs">({method.users})</span>}
              </div>
            ))}
          </motion.div>

          {/* Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-8"
          >
            <ComparisonTable lang={language} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 font-medium transition-colors"
            >
              {getText('View Full Pricing', 'Tingnan ang Buong Pricing')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Compliance ── */}
      <section className="relative z-10 py-20 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp} className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Compliance-First', 'Compliance-First')}
            </h2>
            <p className="text-slate-400">
              {getText('Engineered for Philippine regulatory standards', 'Dinisenyo para sa mga Philippine regulatory standards')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {complianceBadges.map((badge) => (
              <motion.div
                key={badge.name}
                variants={fadeInUp}
                whileHover={{ borderColor: 'rgba(20,184,166,0.3)' }}
                className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 text-center backdrop-blur-sm transition-all duration-300"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <div className="text-white font-medium text-sm">{badge.name}</div>
                <div className="text-slate-500 text-xs">{badge.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={scaleIn}
            className="relative overflow-hidden bg-gradient-to-b from-slate-800/50 to-slate-900/50 border border-teal-700/20 rounded-3xl p-8 md:p-12 text-center backdrop-blur-sm"
          >
            {/* Subtle glow */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(20,184,166,0.08),transparent_70%)] pointer-events-none" />

            <h2 className="relative text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Ready to Start?', 'Handa Ka Na Ba?')}
            </h2>
            <p className="relative text-slate-400 mb-8 max-w-lg mx-auto">
              {getText(
                'Built for Philippine lending apps that need reliable borrower verification without enterprise pricing. Create an account and run your first verification today.',
                'Ginawa para sa mga Philippine lending app na nangangailangan ng maaasahang borrower verification nang walang enterprise pricing. Gumawa ng account at simulan ang iyong unang verification ngayon.'
              )}
            </p>
            <div className="relative flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                type="button"
                whileHover={{ scale: 1.03, boxShadow: '0 0 24px rgba(20,184,166,0.25)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsDemoModalOpen(true)}
                className="px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-xl font-semibold transition-all"
              >
                {getText('Schedule Demo', 'Mag-schedule ng Demo')}
              </motion.button>
              <Link
                to="/register"
                className="px-8 py-4 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl font-semibold hover:bg-slate-700 transition-colors text-center"
              >
                {getText('Create Free Account', 'Gumawa ng Libreng Account')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modals */}
      <BookDemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
      <ContactSalesModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </div>
  );
}
